import { useState, type KeyboardEvent } from 'react';
import type {
  AskUserInput,
  AskUserOutput,
  AskUserQuestion,
} from '@/api/ai/ai.types';

interface AssistantAskUserProps {
  input: AskUserInput;
  onSubmit: (output: AskUserOutput) => void;
}

interface QuestionState {
  selected: Set<string>;
  otherText: string;
  otherActive: boolean;
}

function initState(questions: AskUserQuestion[]): QuestionState[] {
  return questions.map(() => ({
    selected: new Set(),
    otherText: '',
    otherActive: false,
  }));
}

function QuestionCard({
  question,
  state,
  onChange,
}: {
  question: AskUserQuestion;
  state: QuestionState;
  onChange: (next: QuestionState) => void;
}) {
  const toggleOption = (label: string) => {
    const next = new Set(state.selected);
    if (question.multiSelect) {
      if (next.has(label)) next.delete(label);
      else next.add(label);
    } else {
      next.clear();
      next.add(label);
    }
    onChange({ ...state, selected: next, otherActive: false, otherText: '' });
  };

  const activateOther = () => {
    if (question.multiSelect) {
      onChange({ ...state, otherActive: !state.otherActive });
    } else {
      onChange({
        selected: new Set(),
        otherActive: true,
        otherText: state.otherText,
      });
    }
  };

  const handleOtherKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  return (
    <div className="eos-asst-aq-q">
      <span className="eos-asst-aq-label">{question.question}</span>
      <div className="eos-asst-aq-opts">
        {question.options.map((opt) => {
          const active = state.selected.has(opt.label);
          return (
            <button
              key={opt.label}
              type="button"
              className={`eos-asst-aq-opt${active ? ' is-active' : ''}`}
              onClick={() => toggleOption(opt.label)}
            >
              <span className="eos-asst-aq-opt-label">{opt.label}</span>
              {opt.description && (
                <span className="eos-asst-aq-opt-desc">{opt.description}</span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          className={`eos-asst-aq-opt is-other${state.otherActive ? ' is-active' : ''}`}
          onClick={activateOther}
        >
          <span className="eos-asst-aq-opt-label">Sonstiges</span>
        </button>
      </div>
      {state.otherActive && (
        <input
          type="text"
          className="eos-asst-aq-other"
          placeholder="Eigene Antwort eingeben…"
          autoFocus
          value={state.otherText}
          onChange={(e) => onChange({ ...state, otherText: e.target.value })}
          onKeyDown={handleOtherKeyDown}
        />
      )}
    </div>
  );
}

function isComplete(questions: AskUserQuestion[], states: QuestionState[]) {
  return questions.every((q, i) => {
    const s = states[i];
    if (s.otherActive) return s.otherText.trim().length > 0;
    return s.selected.size > 0;
  });
}

function buildOutput(
  questions: AskUserQuestion[],
  states: QuestionState[],
): AskUserOutput {
  return {
    answers: questions.map((q, i) => {
      const s = states[i];
      const selected = [...s.selected];
      if (s.otherActive && s.otherText.trim()) {
        selected.push(s.otherText.trim());
      }
      return { question: q.question, selected };
    }),
  };
}

export function AssistantAskUser({ input, onSubmit }: AssistantAskUserProps) {
  const [states, setStates] = useState(() => initState(input.questions));

  const updateQuestion = (index: number, next: QuestionState) => {
    setStates((prev) => prev.map((s, i) => (i === index ? next : s)));
  };

  const canSubmit = isComplete(input.questions, states);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(buildOutput(input.questions, states));
  };

  return (
    <div className="eos-asst-aq">
      {input.questions.map((q, i) => (
        <QuestionCard
          key={q.question}
          question={q}
          state={states[i]}
          onChange={(next) => updateQuestion(i, next)}
        />
      ))}
      <button
        type="button"
        className="eos-asst-aq-submit"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        Antworten senden
      </button>
    </div>
  );
}

export function AssistantAskUserAnswered({
  output,
}: {
  output: AskUserOutput;
}) {
  return (
    <div className="eos-asst-aq is-answered">
      {output.answers.map((a) => (
        <div key={a.question} className="eos-asst-aq-q">
          <span className="eos-asst-aq-label">{a.question}</span>
          <div className="eos-asst-aq-opts">
            {a.selected.map((s) => (
              <span key={s} className="eos-asst-aq-opt is-active is-locked">
                <span className="eos-asst-aq-opt-label">{s}</span>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
